import styled from "styled-components"
import React, { useEffect, useState } from "react"
import colors from "assets/styles/colors"
import media from "assets/styles/media"

type props = {
  left?: boolean | undefined
  right?: boolean | undefined
  disabled?: boolean
  onClick: React.MouseEventHandler<HTMLButtonElement>
  pixelsToChange?: any
  colorConfig?: any
  icon?: React.ReactNode
  color?: string
  activeColor?: string
  ariaLabel?: string
}

const PixelButton: React.FC<props> = ({
  left = false,
  right,
  colorConfig,
  onClick,
  disabled,
  ariaLabel,
  pixelsToChange = [
    { from: [0, 9], to: [-1, 10] },
    { from: [1, 8], to: [1, 10] },
    { from: [2, 9], to: [0, 11] },
  ],
  icon,
  color = colors.blue400,
  activeColor = colors.black50,
}) => {
  const [pixels, setPixels] = useState([])
  const [hover, setHover] = useState(null)
  const [active, setActive] = useState(false)
  const pixelWidth = 10
  const border = 4

  const makeGrid = () => {
    const tempRows = []
    for (let row = 0; row < pixelWidth + border; row++) {
      const tempRow = []
      for (let rec = 0; rec < pixelWidth + border; rec++) {
        //check if its a movable sqaure
        let sq = (
          <rect
            dur={0.4}
            key={rec + row + "pixel-inpixels"}
            width={`${100 / (pixelWidth + border)}%`}
            height={`${100 / (pixelWidth + border)}%`}
            x={`${(100 / (pixelWidth + border)) * rec}%`}
            y={`${(100 / (pixelWidth + border)) * row}%`}
            fill={
              row < border / 2 ||
              rec < border / 2 ||
              row > pixelWidth + 1 ||
              rec > pixelWidth + 1
                ? "transparent"
                : active
                ? activeColor
                : color
            }
          />
        )

        pixelsToChange.forEach(pixelToChange => {
          if (
            pixelToChange.from[0] + border / 2 === row &&
            pixelToChange.from[1] + border / 2 === rec
          ) {
            sq = (
              <rect
                dur={0.4}
                key={rec + row + "pixel-inpixels"}
                width={`${100 / (pixelWidth + border)}%`}
                height={`${100 / (pixelWidth + border)}%`}
                x={
                  !hover
                    ? `${
                        (100 / (pixelWidth + border)) *
                        (pixelToChange.to[1] + border / 2)
                      }%`
                    : `${(100 / (pixelWidth + border)) * rec}%`
                }
                y={
                  !hover
                    ? `${
                        (100 / (pixelWidth + border)) *
                        (pixelToChange.to[0] + border / 2)
                      }%`
                    : `${(100 / (pixelWidth + border)) * row}%`
                }
                fill={active ? activeColor : color}
                className="pix-square"
              />
            )
          }
        })

        tempRow.push(sq)
      }
      tempRows.push(tempRow)
    }
    setPixels(tempRows)
  }

  useEffect(() => {
    makeGrid()
  }, [hover, active])

  return (
    <PixelBtn
      color={color}
      disabled={disabled}
      onClick={onClick}
      onMouseDownCapture={() => setActive(true)}
      onMouseUpCapture={() => setActive(false)}
      onMouseLeave={() => setActive(false)}
      left={left}
      aria-label={ariaLabel ? ariaLabel : "button"}
    >
      <HoverWindow
        onMouseEnter={e => {
          setHover(true)
        }}
        onMouseLeave={() => setHover(false)}
      />
      {icon && <Icon left={left}>{icon}</Icon>}
      <ReduceBorder>
        <svg
          viewBox="0 0 1 1"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          shapeRendering={"crispEdges"}
          dur={0.4}
        >
          {pixels}
        </svg>
      </ReduceBorder>
    </PixelBtn>
  )
}

export default PixelButton

const Icon = styled.div`
  padding: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  cursor: pointer;
  svg,
  img {
    transition: 0.4s;
    transform: ${props => {
      return props.left ? `rotate(180deg) !important` : null
    }};
  }
`

const PixelBtn = styled.button`
  ${media.tablet} {
    width: 7.813vw;
    height: 7.813vw;
  }
  rect {
    transition: 0.4s;
  }
  padding: 0;
  appearance: none;
  background: none;
  border: none;
  -webkit-appearance: none;
  outline: none;
  position: relative;
  width: 2.778vw;
  height: 2.778vw;
  display: flex;
  justify-content: center;
  align-items: center;
  :active {
    ${Icon} {
      svg,
      img {
        transform: ${props => {
          return props.left
            ? `rotate(180deg) translateX(5px) !important`
            : `translateX(5px)`
        }};
      }
    }
  }
  :disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  ${media.mobile} {
    width: 16vw;
    height: 16vw;
  }
`

const HoverWindow = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;
  cursor: pointer;
`

const ReduceBorder = styled.div`
  position: absolute;
  width: calc(100% * 1.4);
  height: calc(100% * 1.4);

  .pix-square {
    transition: 0.4s;
  }
`
